import { useCallback, useRef, useState } from 'react';
import { apiFetch } from '../utils/api';
import DeleteUsageConfirmModal from '../components/DeleteUsageConfirmModal';

/**
 * Fetches feature/value usage and shows a confirmation modal before delete.
 * @returns {{ confirmDelete: Function, modal: JSX.Element }}
 */
export default function useUsageDeleteConfirm() {
    const [state, setState] = useState(null);
    const resolveRef = useRef(null);

    const close = useCallback((confirmed) => {
        if (resolveRef.current) {
            resolveRef.current(confirmed);
            resolveRef.current = null;
        }
        setState(null);
    }, []);

    const confirmDelete = useCallback(async ({ kind, featureId, valueId, name }) => {
        const url = kind === 'feature'
            ? `/api/features/${featureId}/usage`
            : `/api/features/${featureId}/values/${valueId}/usage`;

        setState({
            kind,
            name,
            usages: [],
            loading: true,
            error: null
        });

        const confirmed = await new Promise((resolve) => {
            resolveRef.current = resolve;

            apiFetch(url)
                .then(async (res) => {
                    if (!res.ok) {
                        const text = await res.text();
                        throw new Error(text || `Failed to look up usage (${res.status})`);
                    }
                    return res.json();
                })
                .then((data) => {
                    const raw = data.usages || data.Usages || [];
                    const usages = raw.map((u) => ({
                        familyId: u.familyId ?? u.FamilyId ?? null,
                        familyName: u.familyName ?? u.FamilyName ?? 'Unmapped',
                        ruleSetId: u.ruleSetId ?? u.RuleSetId,
                        ruleSetName: u.ruleSetName ?? u.RuleSetName,
                        rowCount: u.rowCount ?? u.RowCount ?? 0
                    }));
                    setState((prev) => prev ? {
                        ...prev,
                        usages,
                        loading: false,
                        error: null
                    } : null);
                })
                .catch((err) => {
                    setState((prev) => prev ? {
                        ...prev,
                        loading: false,
                        error: err.message || 'Failed to look up usage.'
                    } : null);
                });
        });

        return confirmed;
    }, []);

    const modal = (
        <DeleteUsageConfirmModal
            isOpen={!!state}
            kind={state?.kind}
            name={state?.name}
            usages={state?.usages}
            loading={state?.loading}
            error={state?.error}
            onConfirm={() => close(true)}
            onClose={() => close(false)}
        />
    );

    return { confirmDelete, modal };
}
